import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {useToast} from './Toast';
import {Conversion} from '../domain/types';
import {downloadText} from '../lib/browserFiles';

import {useOwnedProjectId} from './useOwnedProjectId';
import {
  sortedConversionsSelector,
  sortedProjectConversionsSelector,
  conversionsLoadingSelector,
  conversionsErrorSelector,
  conversionSourcesSelector,
  isMediaReferenceUnrecorded,
  describeRegenerateBlock,
} from '../store/selectors/conversions.selectors';
import {transcriptionIsLoadingSelector} from '../store/selectors/transcription.selectors';
import {audioIsLoadingSelector} from '../store/selectors/audio.selectors';
import {
  fetchConversions,
  fetchProjectConversions,
  deleteConversion,
  downloadConversionXml,
  convertCsvToXml,
} from '../store/slices/conversionsThunks';
import {fetchTranscriptions} from '../store/slices/transcriptionsThunks';
import {fetchAudioUploads} from '../store/slices/audioThunks';
import {setError} from '../store/slices/conversionsSlice';
import {apiErrorMessage, isApiError} from '../lib/apiError';

import ConversionsToolbar, {ConversionScope, ConversionsSummary} from './conversions/ConversionsToolbar';
import ConversionsTable from './conversions/ConversionsTable';
import {conversionFileName} from './conversions/ConversionRow';
import RegenerateConversionModal, {RegenerateSubmission} from './conversions/RegenerateConversionModal';
import {DeleteConfirmationModal} from './DeleteConfirmationModal';

/**
 * The stored marker mode, back to the value the API takes when the conversion is repeated.
 *
 * Not the inverse of the way out: `markers` is stored in two spellings for the same mode, and the
 * cast is the one both existing callers make — the request type still says `word`, but `with-word`
 * is what the API takes and what this conversion was made with.
 */
function repeatMarkerMode(markers: string): 'word' | 'clean' | null {
  const stored = markers === 'clean'
    ? 'clean'
    : (markers === 'with-word' || markers === 'with censored word') ? 'with-word' : null;

  return stored as 'word' | 'clean' | null;
}

/**
 * The conversion history: one row per XML, and the only thing you can do to one is take it or drop it.
 *
 * Nothing is created here — conversions come out of the workspace — so this page is a table, not a
 * screen with a primary action. The desktop-era version treated it as a dashboard: four stat cards,
 * both R2 keys printed in full under every row, and five badge colours competing with the buttons.
 */
const ConversionsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {showToast} = useToast();

  const {projectId: routeProjectId} = useParams<{projectId: string}>();
  // Only a project this account owns reaches the API — see `useOwnedProjectId`.
  const {projectId, project, isResolving: isResolvingProject} = useOwnedProjectId(routeProjectId);
  const allConversions = useAppSelector(sortedConversionsSelector);
  const projectConversions = useAppSelector(sortedProjectConversionsSelector);
  const isLoading = useAppSelector(conversionsLoadingSelector);
  const error = useAppSelector(conversionsErrorSelector);
  // What a flagged row would have to be converted again from, joined by the CSV's R2 key.
  const sources = useAppSelector(conversionSourcesSelector);
  const isLoadingTranscriptions = useAppSelector(transcriptionIsLoadingSelector);
  const isLoadingAudio = useAppSelector(audioIsLoadingSelector);

  const [scope, setScope] = useState<ConversionScope>(projectId ? 'project' : 'all');
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [conversionToDelete, setConversionToDelete] = useState<Conversion | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [conversionToRepeat, setConversionToRepeat] = useState<Conversion | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);

  // A project in the URL is the only thing that makes "this project" a different answer to "all".
  const canScope = Boolean(projectId);
  const effectiveScope: ConversionScope = canScope ? scope : 'all';

  const refreshConversions = useCallback(() => {
    // Wait until the requested project is known to be this account's before asking for anything.
    if (isResolvingProject) {
      return;
    }
    if (effectiveScope === 'project' && projectId) {
      dispatch(fetchProjectConversions(projectId));
    } else {
      dispatch(fetchConversions());
    }
  }, [effectiveScope, projectId, isResolvingProject, dispatch]);

  useEffect(() => {
    refreshConversions();
  }, [refreshConversions]);

  useEffect(() => {
    if (isResolvingProject) {
      return;
    }
    // Unscoped even when the table is showing one project: these two lists are shared with the
    // header's running-job pill and the poller, and narrowing them here would hide a job running in
    // another project. A superset costs nothing — the join below is by key, not by position.
    dispatch(fetchTranscriptions('all'));
    dispatch(fetchAudioUploads('all'));
  }, [isResolvingProject, dispatch]);

  const conversions = effectiveScope === 'project' ? projectConversions : allConversions;

  const filteredConversions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query
      ? conversions.filter(conversion =>
        conversionFileName(conversion).toLowerCase().includes(query)
        || conversion.wordListNames.some(name => name.toLowerCase().includes(query)))
      : conversions;
  }, [conversions, search]);

  const summary = useMemo(() => conversions.reduce(
    (totals, conversion) => ({
      censoredWords: totals.censoredWords + conversion.censoredWords,
      totalCuts: totals.totalCuts + conversion.totalCuts,
    }),
    {censoredWords: 0, totalCuts: 0}
  ), [conversions]);

  /**
   * The rows made before the backend recorded a media reference, mapped to why each one cannot be
   * converted again (`null` when it can).
   *
   * Held back until both source lists have landed. For the first moment after mount there are no
   * transcriptions in the store, and every flagged row would read as though the transcription
   * behind it had been deleted — a wrong reason is worse than one that arrives a beat late.
   */
  const mediaNotices = useMemo(() => {
    const notices = new Map<string, string | null>();

    if (isLoadingTranscriptions || isLoadingAudio) {
      return notices;
    }

    conversions.forEach((conversion) => {
      if (!isMediaReferenceUnrecorded(conversion)) {
        return;
      }
      notices.set(conversion.id, describeRegenerateBlock(conversion, sources.get(conversion.csvPath)));
    });

    return notices;
  }, [conversions, sources, isLoadingTranscriptions, isLoadingAudio]);

  const repeatSource = conversionToRepeat ? sources.get(conversionToRepeat.csvPath) ?? null : null;

  /**
   * Run one legacy conversion again, against the same CSV, word list and marker mode.
   *
   * The old XML is left where it is: it is probably fine, the user may already have it in a
   * project, and this page has a delete button of its own if they decide otherwise.
   */
  const handleRegenerate = async ({mediaReference, fps}: RegenerateSubmission) => {
    const conversion = conversionToRepeat;

    if (!conversion) {
      return;
    }

    const source = sources.get(conversion.csvPath);
    const blockedReason = describeRegenerateBlock(conversion, source);

    // Checked again rather than trusted: the dialog outlives a refetch, and the same call is what
    // narrows the two optional numbers below — nothing reaches the request without them.
    if (blockedReason || !source || source.sampleRate === undefined || source.duration === undefined) {
      showToast(blockedReason ?? 'That conversion can no longer be run again.', 'error', 6000);
      setConversionToRepeat(null);
      return;
    }

    setConversionToRepeat(null);
    setRegeneratingId(conversion.id);

    try {
      const result = await dispatch(convertCsvToXml({
        csvKey: conversion.csvPath,
        audioPath: mediaReference,
        audioSampleRate: source.sampleRate,
        audioDuration: source.duration,
        // The conversion's own project, not the page's — this list can be showing every project.
        projectId: conversion.projectId,
        // Beep sounds are not available on the web yet, and `describeRegenerateBlock` refuses a
        // conversion that used one rather than quietly handing back a different edit.
        mode: 'silence',
        markerMode: repeatMarkerMode(conversion.markers),
        // Every list the original used, not just the first — a conversion made from a set of five
        // has to be repeatable as that same set, and `wordListId` is only ever the first of them.
        wordlistIds: conversion.wordListIds,
        fps,
        outputFileName: conversionFileName(conversion),
      })).unwrap();

      showToast(
        `Converted again — ${conversionFileName(result.conversion)} · `
        + `${result.metadata.censored_words} words censored`,
        'success',
        6000
      );
      refreshConversions();
    } catch (cause) {
      // A word list used by an old conversion may well have been deleted since — that used to be
      // ignored server-side (producing an XML with nothing censored), and is now a refusal. There
      // is no picker on this dialog to fix it in, so the message is the whole answer and is shown
      // as written rather than folded into the generic sentence.
      if (isApiError(cause) && cause.reason === 'wordlist_not_found') {
        showToast(cause.message, 'error', 10000);
      } else {
        showToast(`Could not convert that again: ${apiErrorMessage(cause)}`, 'error', 6000);
      }
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleDownload = async (conversion: Conversion) => {
    setDownloadingId(conversion.id);

    const result = await dispatch(downloadConversionXml({conversionId: conversion.id}));

    setDownloadingId(null);

    if (downloadConversionXml.fulfilled.match(result)) {
      const fileName = conversionFileName(conversion);
      downloadText(result.payload, fileName, 'application/xml;charset=utf-8');
      showToast(`Downloaded ${fileName}`, 'success');
      return;
    }

    showToast('Could not download that XML', 'error');
  };

  const handleConfirmDelete = async () => {
    if (!conversionToDelete) {
      return;
    }

    setIsDeleting(true);
    try {
      await dispatch(deleteConversion(conversionToDelete.id)).unwrap();
      showToast('Conversion deleted', 'success');
    } catch {
      showToast('Could not delete that conversion', 'error');
    } finally {
      setIsDeleting(false);
      setConversionToDelete(null);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-950">
      <div className="mx-auto max-w-6xl px-4 py-6 space-y-5">
        <ConversionsToolbar
          projectName={project?.name ?? null}
          scope={effectiveScope}
          onScopeChange={(nextScope) => {
            setScope(nextScope);
            setSearch('');
          }}
          canScope={canScope}
          error={error}
          onDismissError={() => dispatch(setError(null))}
        />

        {conversions.length > 0 && (
          <ConversionsSummary
            total={conversions.length}
            censoredWords={summary.censoredWords}
            totalCuts={summary.totalCuts}
          />
        )}

        <ConversionsTable
          conversions={filteredConversions}
          isLoading={isLoading}
          search={search}
          onSearchChange={setSearch}
          onGoToWorkspace={() => navigate(projectId ? `/projects/${projectId}/workspace` : '/workspace')}
          downloadingId={downloadingId}
          mediaNotices={mediaNotices}
          regeneratingId={regeneratingId}
          onDownload={handleDownload}
          onDelete={setConversionToDelete}
          onRegenerate={setConversionToRepeat}
        />

        <p className="max-w-2xl px-1 text-xs text-gray-600">
          Deleting a conversion removes the XML and this record. The transcription it came from stays
          put, so you can always convert it again — against a different word list if you need to.
        </p>
      </div>

      <RegenerateConversionModal
        conversion={conversionToRepeat}
        source={repeatSource}
        isConverting={regeneratingId !== null}
        onCancel={() => setConversionToRepeat(null)}
        onConfirm={handleRegenerate}
      />

      <DeleteConfirmationModal
        isOpen={conversionToDelete !== null}
        listName={conversionToDelete ? conversionFileName(conversionToDelete) : null}
        isDeleting={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConversionToDelete(null)}
        title="Delete this conversion?"
        message="This removes the XML and its record. The transcription behind it is kept. Delete"
      />
    </div>
  );
};

export default ConversionsContainer;
